/* eslint-disable react/jsx-pascal-case */
import { GetStaticProps } from 'next';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import React from 'react';
import { useLocation } from 'react-use';

import Banner from '@/components/Chinese/Banner';
import Blog from '@/components/Chinese/Blog';
import Course from '@/components/Chinese/Course';
import CoursePackage from '@/components/Chinese/CoursePackage';
import LearningExperience from '@/components/Chinese/LearningExperience';
import PersonalCoach from '@/components/Chinese/PersonalCoach';
import PublicPraise from '@/components/Chinese/PublicPraise';
import Teacher from '@/components/Chinese/Teacher';
import CTA from '@/components/CTA';
import Layout from '@/components/Layout';

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'home',
        'path',
        'form',
        'chinese',
      ])),
    },
  };
};

const Chinese = () => {
  const { t } = useTranslation(['home', 'common', 'path', 'chinese']);
  const state = useLocation();
  return (
    <>
      <NextSeo
        openGraph={{
          url: state?.href,
          title: t('common:seo-中文课程-title'),
          description: t('common:seo-中文课程-description'),
          images: [
            {
              url: 'https://cdnwukong.com/images/public/og/offical-site-1.png',
              width: 300,
              height: 300,
              alt: t('common:seo-中文课程-title'),
            },
          ],
        }}
      />
      <Head>
        <title>{t('common:seo-中文课程-title')}</title>
        <meta
          name="description"
          content={t('common:seo-中文课程-description')}
        />
        <meta name="keywords" content={t('common:seo-中文课程-关键词')} />
        <meta
          data-n-head="ssr"
          data-hid="apple-mobile-web-app-title"
          name="apple-mobile-web-app-title"
          content={t('common:seo-中文课程-title')}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Course',
              name: t('common:seo-悟空中文'), //填写当前学科页面的(WuKong Chinese/WuKong Math/WuKong ELA Class), 对应中文为（悟空中文、悟空数学、悟空英语阅读写作）
              description: t('common:seo-中文课程-description'), //填写当前学科页面的meta description
              provider: {
                '@type': 'Organization',
                name: t('common:seo-悟空教育'), //中文为悟空教育
                url: state?.href, //首页url， 区分中文英文
              },
              educationalLevel: t('common:seo-中文-年龄'), //中文为3-18岁
              learningResourceType: t('common:seo-中文-资源类型'), //1v1在线中文直播课
              url: state?.href, //当前学科页url
              mainEntityOfPage: {
                '@type': 'WebPage',
                url: state?.href, //当前学科页url
              },
              potentialAction: {
                '@type': 'ReserveAction',
                name: t('common:seo-中文-约课名称'), //填写对应学科页的约课名称，见下表
                target: {
                  '@type': 'EntryPoint',
                  urlTemplate: t('common:seo-中文-约课链接'), //填写对应学科页的约课链接，见下表
                  actionPlatform: [
                    'https://schema.org/DesktopWebPlatform',
                    'https://schema.org/MobileWebPlatform',
                  ],
                },
                description: t('common:seo-中文-约课描述'), //填写对应学科页的约课描述，见下表
              },
              offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
              },
            }),
          }}
        />
      </Head>
      <main className="relative overflow-hidden">
        {/* pc 拆减两边 移动端铺满 */}
        <div className="absolute left-1/2 top-0 h-[758px] w-full translate-x-[-50%]  bg-[url('/images/home/bg-m.svg')] bg-cover  bg-center bg-no-repeat md:h-[4960px] md:w-[1920px] md:bg-[url('/images/chinese/chinese-bg.svg')]" />
        <div className="relative z-10 bg-transparent">
          <Banner />
          {/* 私教课 */}
          <PersonalCoach />
          {/* 最优秀的中文老师 */}
          <Teacher />
          {/* 分级课程 */}
          <Course />
          {/* 超越预期的学习体验 */}
          <LearningExperience />
          {/* 口碑之选 */}
          <PublicPraise />
          {/* 课包 */}
          <CoursePackage />
          {/* 博客 */}
          <Blog />
          <section className="marginModelTop mb-20 lg:mb-[180px] xl:mb-[240px]">
            <CTA
              blockTitle="步骤之旅-标题"
              steps1name="步骤之旅-步骤-1"
              steps2name="步骤之旅-步骤-2"
              steps3name="步骤之旅-步骤-3"
              useTranslationName="chinese"
              subject="chinese"
            />
          </section>
        </div>
      </main>
    </>
  );
};

export default Chinese;

Chinese.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>;
